import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import {
  getDashboardIntegration,
  provisionDashboardIntegration,
  rotateDashboardIntegrationSecret,
} from "../services/dashboardApi";

const formatDate = (value) => {
  if (!value) return "Never";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }

  return date.toLocaleString();
};

const DevelopersLayer = () => {
  const [integration, setIntegration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState(null);
  const [showProvisionModal, setShowProvisionModal] = useState(false);
  const [showNewKey, setShowNewKey] = useState(null);
  const [copied, setCopied] = useState("");
  const [showRotateModal, setShowRotateModal] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [rotateError, setRotateError] = useState(null);

  const copyText = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);

      setTimeout(() => {
        setCopied("");
      }, 1600);
    } catch {
      setCopied("");
    }
  };

  const loadIntegration = async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const response = await getDashboardIntegration();
      setIntegration(response?.integration || null);
    } catch (error) {
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIntegration();
  }, []);

  const provisionIntegration = async () => {
    if (provisioning || integration?.connected) {
      return;
    }

    setProvisioning(true);
    setProvisionError(null);

    try {
      const response = await provisionDashboardIntegration();

      const nextIntegration = response?.integration || null;
      const secretKey =
        nextIntegration?.secretKey ||
        response?.secretKey ||
        response?.secret_key ||
        response?.credentials?.secretKey ||
        null;

      const publicKey =
        response?.publicKey ||
        response?.public_key ||
        response?.credentials?.publicKey ||
        nextIntegration?.publicKey ||
        null;

      if (!secretKey) {
        throw new Error(
          "Simplif activated the integration but did not return the one-time secret."
        );
      }

      setIntegration(
        nextIntegration || {
          connected: false,
          status: "unprovisioned",
          publicKey,
          provisionedAt: new Date().toISOString(),
        }
      );

      setShowProvisionModal(false);

      const generatedKeys = {
        secretKey,
        publicKey,
      };

      setShowNewKey(generatedKeys);
      sessionStorage.setItem("simplifLatestApiKeys", JSON.stringify(generatedKeys));
      window.dispatchEvent(new Event("simplif-api-keys-updated"));
    } catch (error) {
      setProvisionError(error);
    } finally {
      setProvisioning(false);
    }
  };

  const rotateIntegrationSecret = async () => {
    if (rotating || !integration?.provisionedAt) {
      return;
    }

    setRotating(true);
    setRotateError(null);

    try {
      const response = await rotateDashboardIntegrationSecret();
      const nextIntegration = response?.integration || null;

      const secretKey =
        nextIntegration?.secretKey ||
        response?.secretKey ||
        response?.secret_key ||
        response?.credentials?.secretKey ||
        null;

      const publicKey =
        nextIntegration?.publicKey ||
        response?.publicKey ||
        response?.public_key ||
        response?.credentials?.publicKey ||
        integration?.publicKey ||
        null;

      if (!secretKey) {
        throw new Error(
          "Simplif rotated the secret but did not return the new one-time secret."
        );
      }

      setIntegration((current) => ({
        ...current,
        ...(nextIntegration || {}),
        connected: current?.connected === true,
        status: current?.status || "unprovisioned",
        publicKey,
        lastRotatedAt:
          nextIntegration?.lastRotatedAt ||
          new Date().toISOString(),
      }));

      setShowRotateModal(false);

      const rotatedKeys = {
        secretKey,
        publicKey,
        mode: "rotation",
      };

      setShowNewKey(rotatedKeys);
      sessionStorage.setItem("simplifLatestApiKeys", JSON.stringify(rotatedKeys));
      window.dispatchEvent(new Event("simplif-api-keys-updated"));
    } catch (error) {
      setRotateError(error);
    } finally {
      setRotating(false);
    }
  };

  const connected = integration?.connected === true;
  const credentialsGenerated = Boolean(integration?.provisionedAt);
  const canManage = integration?.canManage !== false;

  return (
    <>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
        <div>
          <h4 className="mb-4">Developers</h4>

          <p className="text-secondary-light mb-0">
            Integrate Simplif into your application and manage API access.
          </p>
        </div>


      </div>

      <div className="card mb-24">
        <div className="card-header border-bottom bg-base px-24 py-20">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <h5 id="api-integration" className="mb-4">API Integration</h5>

              <p className="text-secondary-light text-sm mb-0">
                Server-side credentials for your Simplif production integration.
              </p>
            </div>

            {!loading && !loadError && (
              <span
                className={`px-12 py-6 rounded-pill text-sm fw-semibold ${
                  connected
                    ? "bg-success-focus text-success-main"
                    : "bg-warning-focus text-warning-main"
                }`}
              >
                {connected ? "Connected" : "Not connected"}
              </span>
            )}
          </div>
        </div>

        <div className="card-body p-24">
          {loading && (
            <div className="d-flex align-items-center gap-3 py-12">
              <div
                className="spinner-border spinner-border-sm text-primary-600"
                role="status"
              >
                <span className="visually-hidden">
                  Loading integration...
                </span>
              </div>

              <span className="text-secondary-light">
                Loading API integration...
              </span>
            </div>
          )}

          {!loading && loadError && (
            <div className="bg-danger-focus border border-danger-200 radius-8 p-16">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div>
                  <h6 className="text-danger-main mb-4">
                    We couldn't load your integration
                  </h6>

                  <p className="text-secondary-light text-sm mb-0">
                    {loadError?.message ||
                      "Integration status is temporarily unavailable."}
                  </p>
                </div>

                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={loadIntegration}
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {!loading && !loadError && !connected && (
            <div className="row gy-4 align-items-center">
              <div className="col-lg-8">
                <div className="d-flex gap-3">
                  <div className="w-48-px h-48-px bg-primary-50 text-primary-600 rounded-circle d-flex align-items-center justify-content-center flex-shrink-0">
                    <Icon
                      icon="solar:plug-circle-outline"
                      className="text-2xl"
                    />
                  </div>

                  <div>
                    <h6 className="mb-6">
                      {credentialsGenerated ? "API credentials generated" : "Connect your production API"}
                    </h6>

                    <p className="text-secondary-light mb-0">
                      {credentialsGenerated
                        ? "Your credentials are ready. Send your first authenticated signup evaluation to complete the connection."
                        : "Generate your production credentials to start sending signup evaluations to Simplif."}
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-lg-4 text-lg-end">
                {credentialsGenerated ? (
                  <div className="d-flex flex-wrap align-items-center justify-content-lg-end gap-2">
                    <span className="px-12 py-6 rounded-pill text-sm fw-semibold bg-warning-focus text-warning-main">
                      Waiting for connection
                    </span>
                    {canManage && (
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => {
                          setRotateError(null);
                          setShowRotateModal(true);
                        }}
                      >
                        <Icon icon="solar:key-minimalistic-square-2-outline" className="me-1" />
                        Rotate Secret
                      </button>
                    )}
                  </div>
                ) : canManage ? (
                  <button
                    type="button"
                    className="btn btn-primary-600"
                    onClick={() => {
                      setProvisionError(null);
                      setShowProvisionModal(true);
                    }}
                  >
                    Generate credentials
                  </button>
                ) : (
                  <span className="text-secondary-light text-sm">
                    An owner or admin must connect the API.
                  </span>
                )}
              </div>
            </div>
          )}

          {!loading && !loadError && connected && (
            <div className="row gy-4">
              <div className="col-lg-6">
                <label className="form-label fw-semibold">
                  Environment
                </label>

                <div>
                  <span className="px-12 py-6 rounded-pill text-sm fw-semibold bg-success-focus text-success-main">
                    Live
                  </span>
                </div>
              </div>

              <div className="col-lg-6">
                <label className="form-label fw-semibold">
                  Status
                </label>

                <div className="fw-semibold text-success-main">
                  Active
                </div>
              </div>

              <div className="col-lg-6">
                <label className="form-label fw-semibold">
                  Public key
                </label>

                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={integration?.publicKey || "Unavailable"}
                    readOnly
                  />

                  {integration?.publicKey && (
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() =>
                        copyText(
                          integration.publicKey,
                          "public-key"
                        )
                      }
                    >
                      <Icon
                        icon={
                          copied === "public-key"
                            ? "solar:check-circle-outline"
                            : "solar:copy-outline"
                        }
                        className="me-2"
                      />

                      {copied === "public-key"
                        ? "Copied"
                        : "Copy"}
                    </button>
                  )}
                </div>
              </div>

              <div className="col-lg-6">
                <label className="form-label fw-semibold">
                  Secret key
                </label>

                <div className="form-control bg-neutral-50">
                  Stored securely - full secret is not retrievable
                </div>
              </div>

              <div className="col-lg-4">
                <label className="form-label fw-semibold">
                  Provisioned
                </label>

                <div className="text-secondary-light">
                  {formatDate(integration?.provisionedAt)}
                </div>
              </div>

              <div className="col-lg-4">
                <label className="form-label fw-semibold">
                  Last rotated
                </label>

                <div className="text-secondary-light">
                  {formatDate(integration?.lastRotatedAt)}
                </div>
              </div>

              <div className="col-lg-4">
                <label className="form-label fw-semibold">
                  Last used
                </label>

                <div className="text-secondary-light">
                  {formatDate(integration?.lastUsedAt)}
                </div>
              </div>

              {canManage && (
                <div className="col-12">
                  <div className="border-top pt-20 mt-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div>
                      <h6 className="mb-4">Secret key security</h6>
                      <p className="text-secondary-light text-sm mb-0">
                        Rotate the secret if it has been exposed or as part of your credential rotation policy.
                      </p>
                    </div>

                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => {
                        setRotateError(null);
                        setShowRotateModal(true);
                      }}
                    >
                      <Icon
                        icon="solar:restart-outline"
                        className="me-2"
                      />
                      Rotate secret
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="row gy-4">
        <div className="col-lg-7">
          <div className="card h-100">
            <div className="card-header border-bottom bg-base px-24 py-20">
              <h5 className="mb-4">Quick Start</h5>

              <p className="text-secondary-light text-sm mb-0">
                Start evaluating signup risk in four steps.
              </p>
            </div>

            <div className="card-body p-24">
              {[
                {
                  number: "1",
                  title: "Connect your API",
                  text: "Generate your production server-side credentials.",
                },
                {
                  number: "2",
                  title: "Collect signup signals",
                  text: "Collect the email, device and browser signals available during signup.",
                },
                {
                  number: "3",
                  title: "Evaluate the signup",
                  text: "Send the signup to Simplif and receive a fraud decision.",
                },
                {
                  number: "4",
                  title: "Handle the verdict",
                  text: "Allow, review or block the signup based on the returned verdict.",
                },
              ].map((step, index) => (
                <div
                  key={step.number}
                  className={`d-flex gap-3 ${
                    index !== 3 ? "mb-24" : ""
                  }`}
                >
                  <div className="w-40-px h-40-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center fw-bold flex-shrink-0">
                    {step.number}
                  </div>

                  <div>
                    <h6 className="mb-4">
                      {step.title}
                    </h6>

                    <p className="text-secondary-light mb-0">
                      {step.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card h-100">
            <div className="card-header border-bottom bg-base px-24 py-20">
              <h5 className="mb-4">API Endpoint</h5>

              <p className="text-secondary-light text-sm mb-0">
                Production API base URL.
              </p>
            </div>

            <div className="card-body p-24">
              <label className="form-label fw-semibold">
                Base URL
              </label>

              <div className="input-group mb-24">
                <input
                  type="text"
                  className="form-control"
                  value="https://api.simplif.com"
                  readOnly
                />

                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() =>
                    copyText(
                      "https://api.simplif.com",
                      "endpoint"
                    )
                  }
                >
                  <Icon
                    icon={
                      copied === "endpoint"
                        ? "solar:check-circle-outline"
                        : "solar:copy-outline"
                    }
                    className="me-2"
                  />

                  {copied === "endpoint" ? "Copied" : "Copy"}
                </button>
              </div>

              <div className="bg-neutral-50 border radius-8 p-16">
                <div className="d-flex gap-3">
                  <Icon
                    icon="solar:shield-check-outline"
                    className="text-primary-600 text-xl flex-shrink-0 mt-1"
                  />

                  <div>
                    <h6 className="mb-4">
                      Keep secret keys private
                    </h6>

                    <p className="text-secondary-light text-sm mb-0">
                      Secret API keys should only be used from your
                      server. Never expose them in browser or frontend
                      application code.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-top mt-24 pt-24">
                <h6 className="mb-12">
                  Signup endpoint
                </h6>

                <div className="bg-neutral-900 text-white radius-8 p-16">
                  <span className="text-success-main fw-semibold me-12">
                    POST
                  </span>

                  <code className="text-white">
                    /v1/signups/evaluate
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showProvisionModal && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={() => {
              if (!provisioning) {
                setShowProvisionModal(false);
              }
            }}
          />

          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content radius-12 border-0 shadow-lg">
                <div className="modal-header px-24 py-20">
                  <div>
                    <h5 className="modal-title mb-1">
                      Connect production API
                    </h5>

                    <p className="text-secondary-light text-sm mb-0">
                      Simplif will generate one live credential pair.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="btn-close"
                    disabled={provisioning}
                    onClick={() =>
                      setShowProvisionModal(false)
                    }
                  />
                </div>

                <div className="modal-body p-24">
                  <div className="bg-warning-focus border border-warning-200 radius-8 p-16">
                    <div className="d-flex gap-3">
                      <Icon
                        icon="solar:shield-warning-outline"
                        className="text-warning-main text-xl flex-shrink-0"
                      />

                      <div>
                        <h6 className="mb-4">
                          Your secret will be shown once
                        </h6>

                        <p className="text-secondary-light text-sm mb-0">
                          Copy the secret immediately and store it in your
                          server-side secret manager. Simplif does not store
                          the raw secret and cannot show it again.
                        </p>
                      </div>
                    </div>
                  </div>

                  {provisionError && (
                    <div className="bg-danger-focus border border-danger-200 radius-8 p-12 mt-16">
                      <span className="text-danger-main text-sm">
                        {provisionError?.message ||
                          "We couldn't generate your credentials."}
                      </span>
                    </div>
                  )}
                </div>

                <div className="modal-footer px-24 py-20">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={provisioning}
                    onClick={() =>
                      setShowProvisionModal(false)
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary-600"
                    disabled={provisioning}
                    onClick={provisionIntegration}
                  >
                    {provisioning ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        />
                        Generating...
                      </>
                    ) : (
                      "Generate live credentials"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showRotateModal && (
        <>
          <div className="modal-backdrop fade show" />

          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content radius-12 border-0 shadow-lg">
                <div className="modal-header px-24 py-20">
                  <h5 className="modal-title">Rotate secret key</h5>

                  <button
                    type="button"
                    className="btn-close"
                    disabled={rotating}
                    onClick={() => setShowRotateModal(false)}
                  />
                </div>

                <div className="modal-body p-24">
                  <div className="bg-danger-focus border border-danger-200 radius-8 p-16">
                    <div className="d-flex gap-3">
                      <Icon
                        icon="solar:shield-warning-outline"
                        className="text-danger-main text-xl flex-shrink-0"
                      />

                      <div>
                        <h6 className="mb-4">
                          Your current secret will stop working
                        </h6>

                        <p className="text-secondary-light text-sm mb-0">
                          Rotating immediately replaces the existing secret key.
                          Update your server with the new secret before sending
                          any further production API requests.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-warning-focus border border-warning-200 radius-8 p-16 mt-16">
                    <p className="text-sm mb-0">
                      The new secret will be displayed once. Simplif cannot
                      retrieve the complete secret after you close the next window.
                    </p>
                  </div>

                  {rotateError && (
                    <div className="bg-danger-focus border border-danger-200 radius-8 p-12 mt-16">
                      <span className="text-danger-main text-sm">
                        {rotateError?.message ||
                          "We couldn't rotate your secret key."}
                      </span>
                    </div>
                  )}
                </div>

                <div className="modal-footer px-24 py-20">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    disabled={rotating}
                    onClick={() => setShowRotateModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-danger-600"
                    disabled={rotating}
                    onClick={rotateIntegrationSecret}
                  >
                    {rotating ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        />
                        Rotating...
                      </>
                    ) : (
                      "Rotate secret"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showNewKey && (
        <>
          <div className="modal-backdrop fade show" />

          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content radius-12 border-0 shadow-lg">
                <div className="modal-body p-32">
                  <div className="w-56-px h-56-px rounded-circle bg-success-focus text-success-main d-flex align-items-center justify-content-center mb-20">
                    <Icon
                      icon="solar:key-outline"
                      className="text-2xl"
                    />
                  </div>

                  <h5 className="mb-8">
                    {showNewKey.mode === "rotation"
                      ? "Secret key rotated"
                      : "API credentials created"}
                  </h5>

                  <p className="text-secondary-light mb-20">
                    Copy your secret key now. For security, the complete
                    secret cannot be viewed again after you close this window.
                  </p>

                  {showNewKey.publicKey && (
                    <>
                      <label className="form-label fw-semibold">
                        Public key
                      </label>

                      <div className="input-group mb-16">
                        <input
                          type="text"
                          className="form-control"
                          value={showNewKey.publicKey}
                          readOnly
                        />

                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          onClick={() =>
                            copyText(
                              showNewKey.publicKey,
                              "new-public-key"
                            )
                          }
                        >
                          {copied === "new-public-key"
                            ? "Copied"
                            : "Copy"}
                        </button>
                      </div>
                    </>
                  )}

                  <label className="form-label fw-semibold">
                    Secret key
                  </label>

                  <div className="bg-neutral-100 border radius-8 p-16 mb-16">
                    <code
                      style={{
                        wordBreak: "break-all",
                      }}
                    >
                      {showNewKey.secretKey}
                    </code>
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-primary w-100 mb-12"
                    onClick={() =>
                      copyText(
                        showNewKey.secretKey,
                        "new-secret-key"
                      )
                    }
                  >
                    <Icon
                      icon={
                        copied === "new-secret-key"
                          ? "solar:check-circle-outline"
                          : "solar:copy-outline"
                      }
                      className="me-2"
                    />

                    {copied === "new-secret-key"
                      ? "Copied"
                      : "Copy secret key"}
                  </button>

                  <div className="bg-warning-focus border border-warning-200 radius-8 p-12 mb-20">
                    <span className="text-sm">
                      Store this secret securely before continuing. Closing
                      this window permanently removes it from this page.
                    </span>
                  </div>

                  <button
                    type="button"
                    className="btn btn-primary-600 w-100"
                    onClick={() => {
                      setShowNewKey(null);
                      setCopied("");
                    }}
                  >
                    I have stored the secret securely
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default DevelopersLayer;

